import { useState, useEffect, useRef } from 'react';
import { Socket } from 'socket.io-client';

interface LotteryDisplayProps {
  lotteryId: string;
  socket: Socket | null;
}

interface Participant {
  audienceId: string;
  nickname: string;
  x?: number;
  y?: number;
}

interface LotteryResults {
  lotteryId: string;
  winnerId: string | null;
  winnerNickname: string | null;
  winners?: { id: string; nickname: string }[];
}

function LotteryDisplay({ lotteryId, socket }: LotteryDisplayProps) {
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [remainingSeconds, setRemainingSeconds] = useState<number | null>(null);
  const [winnerIds, setWinnerIds] = useState<string[]>([]);
  const [, setWinners] = useState<{ id: string; nickname: string }[]>([]);
  const [showWinnerAnimation, setShowWinnerAnimation] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Genera posizione casuale evitando sovrapposizioni
  const generateRandomPosition = (existingPositions: { x: number; y: number }[]): { x: number; y: number } => {
    const margin = 100; // margine dai bordi
    const maxWidth = window.innerWidth - margin * 2;
    const maxHeight = window.innerHeight - margin * 2;
    const minDistance = 150; // distanza minima tra nomi

    let attempts = 0;
    let x: number, y: number;

    do {
      x = margin + Math.random() * maxWidth;
      y = margin + Math.random() * maxHeight;
      attempts++;

      // Controlla distanza da posizioni esistenti
      const tooClose = existingPositions.some(pos => {
        const dx = pos.x - x;
        const dy = pos.y - y;
        return Math.sqrt(dx * dx + dy * dy) < minDistance;
      });

      if (!tooClose || attempts > 50) break;
    } while (true);

    return { x, y };
  };

  // Ascolta eventi socket
  useEffect(() => {
    if (!socket) return;

    const handleParticipantAdded = (data: { lotteryId: string; audienceId: string; nickname: string }) => {
      if (data.lotteryId !== lotteryId) return;

      setParticipants(prev => {
        // Evita duplicati
        if (prev.some(p => p.audienceId === data.audienceId)) return prev;

        // Genera posizione casuale
        const existingPositions = prev.map(p => ({ x: p.x || 0, y: p.y || 0 }));
        const { x, y } = generateRandomPosition(existingPositions);

        return [...prev, {
          audienceId: data.audienceId,
          nickname: data.nickname,
          x,
          y,
        }];
      });
    };

    const handleTick = (data: { lotteryId: string; remainingSeconds: number }) => {
      if (data.lotteryId === lotteryId) {
        setRemainingSeconds(data.remainingSeconds);
      }
    };

    const handleClosed = (data: LotteryResults) => {
      if (data.lotteryId !== lotteryId) return;

      const winnersData = data.winners || (data.winnerId ? [{ id: data.winnerId, nickname: data.winnerNickname || '' }] : []);
      setWinners(winnersData);
      setWinnerIds(winnersData.map(w => w.id));

      // Avvia animazione vincitore dopo un breve delay
      setTimeout(() => {
        setShowWinnerAnimation(true);
      }, 500);
    };

    socket.on('lottery:participant-added', handleParticipantAdded);
    socket.on('lottery:tick', handleTick);
    socket.on('lottery:closed', handleClosed);

    return () => {
      socket.off('lottery:participant-added', handleParticipantAdded);
      socket.off('lottery:tick', handleTick);
      socket.off('lottery:closed', handleClosed);
    };
  }, [socket, lotteryId]);

  // Animazione vincitore: porta al centro
  const getWinnerStyle = (participant: Participant): React.CSSProperties => {
    const isWinner = winnerIds.includes(participant.audienceId);

    if (!showWinnerAnimation || !isWinner) {
      return {
        left: participant.x,
        top: participant.y,
        transform: 'translate(-50%, -50%)',
        transition: 'all 0.5s ease-out',
      };
    }

    // Posizione verticale per vincitori multipli
    const winnerIndex = winnerIds.indexOf(participant.audienceId);
    const totalWinners = winnerIds.length;
    // Distribuisci verticalmente: centro ± offset
    const spacing = 90; // px tra ogni nome
    const totalHeight = (totalWinners - 1) * spacing;
    const topOffset = 50 + ((winnerIndex * spacing - totalHeight / 2) / window.innerHeight) * 100;

    return {
      left: '50%',
      top: `${topOffset}%`,
      transform: 'translate(-50%, -50%) scale(2)',
      transition: 'all 1.5s ease-in-out',
      zIndex: 100,
    };
  };

  return (
    <div
      ref={containerRef}
      className="h-screen w-screen display-dark-bg relative overflow-hidden"
    >
      {/* Timer in alto */}
      {remainingSeconds !== null && !showWinnerAnimation && (
        <div className="absolute top-8 left-1/2 transform -translate-x-1/2 z-50">
          <div className={`text-6xl font-bold font-heading ${remainingSeconds <= 3 ? 'text-red-500 animate-pulse' : 'text-parchment-400'}`}>
            {remainingSeconds}
          </div>
        </div>
      )}

      {/* Nomi dei partecipanti sparsi */}
      {participants.map((participant) => {
        const isWinner = winnerIds.includes(participant.audienceId);
        const shouldHide = showWinnerAnimation && !isWinner;

        return (
          <div
            key={participant.audienceId}
            className={`absolute font-bold transition-all duration-500 ${
              showWinnerAnimation && isWinner
                ? 'text-yellow-300 font-display text-6xl'
                : shouldHide
                ? 'opacity-20 text-parchment-100 text-2xl'
                : 'text-parchment-100 font-heading text-2xl'
            }`}
            style={getWinnerStyle(participant)}
          >
            {participant.nickname}
          </div>
        );
      })}

      {/* Messaggio "nessun partecipante" se la lotteria è chiusa senza vincitore */}
      {winnerIds.length === 0 && showWinnerAnimation && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-4xl text-parchment-400">Nessun partecipante</div>
        </div>
      )}

      {/* Effetto spotlight sul vincitore */}
      {showWinnerAnimation && winnerIds.length > 0 && (
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full"
            style={{
              background: 'radial-gradient(circle, rgba(139,105,20,0.3) 0%, rgba(0,0,0,0) 70%)',
              animation: 'pulse 2s infinite',
            }}
          />
        </div>
      )}
    </div>
  );
}

export default LotteryDisplay;
